import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Stepper,
  Step,
  StepLabel,
  Box,
  DialogContentText,
  StepButton,
  useMediaQuery,
} from '@mui/material';
import {
  atualizaProduto,
  buscaProdutoCodigo,
  cadastrarProduto,
  listaCategorias,
  listaGruposByCategoria,
  listaProdutos,
  listaUnidadesDeMedidas, verificaCategoria, verificaUnidade
} from './../configs/functions';
import { convertToIsoDate, hasPermission } from '../../../../../configs/functions';
import { useCommonItems } from '../../../../../contexts/CommonItensProvider';

import { StepProductsInfo } from './StepProductsInfo';
import { StepProductVariations } from './StepProductVariations';
import { StepCompaniesSelection } from './StepCompaniesSelection';
import { StepProductConfirm } from './StepProductConfirm';

dayjs.extend(utc);

export function ProductForm({ open, setOpen, selectedProduct, empresas, queryKey, actionsAllowed }) {
  const queryClient = useQueryClient();
  const isEditAction = !!selectedProduct;

  const isMobile = useMediaQuery(theme => theme.breakpoints.down("sm"));

  const { exibirDialog, handleCloseDialog, exibirAlerta } = useCommonItems();

  // O usuário pode interagir com o formulário se:
  // 1. Possuir permissões ('admin', 'admin_*', 'update_*').
  // 2. For uma ação de cadastro (empresas com Módulo Permitido).
  // 3. For uma edição e a flag `actionsAllowed` for verdadeira.
  const allowInteraction =
    hasPermission(['admin', 'admin_produtos', 'update_produtos']) &&
    (!isEditAction || (isEditAction && actionsAllowed));

  const [activeStep, setActiveStep] = useState(0);
  const [exibiAlertaAutoPreenchimento, setExibiAlertaAutoPreenchimento] = useState(false);

  const [categorias, setCategorias] = useState([]);
  const [unidadesDeMedida, setUnidadesDeMedida] = useState([]);
  const [empresasAssociadas, setEmpresasAssociadas] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [GHEs, setGHEs] = useState([]);

  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    control,
    formState: { errors },
    trigger,
    setError,
    clearErrors,
    reset,
    watch
  } = useForm();

  const carregaCategorias = async (empresasId = null) => {
    try {
      // para cadastro fazer o get com as empresas que possuem o modulo Produto !== recebe
      if (!empresasId) {
        empresasId = empresas.map(empresa => empresa.id);
      }

      const response = await listaCategorias(empresasId);
      const categoriesData = response.data.data;

      setCategorias(categoriesData);
    } catch (error) {
      console.log(error);
    }
  };

  const carregaUnidadesDeMedida = async () => {
    try {
      const response = await listaUnidadesDeMedidas();
      setUnidadesDeMedida(response.data.data);
    } catch (error) {
      const unidadeProduto = selectedProduct?.unidadeMedida;
      if (unidadeProduto) {
        const unidadeFormatada = {
          ...unidadeProduto,
          nome: unidadeProduto.nome || unidadeProduto.tipo || 'Unidade não identificada',
          contrato: null
        };
        setUnidadesDeMedida([unidadeFormatada]);
      } else {
        setUnidadesDeMedida([]);
      }
    }
  };

  async function loadInitialData(idEmpresa) {
    try {
      exibirDialog('Carregando dados. Aguarde...');
      await carregaCategorias(idEmpresa);
      await carregaUnidadesDeMedida();
    } catch (error) {
      console.log('error:', error);
      exibirAlerta('Ops', 'Houve um erro ao carregar os dados.', 'error');
    } finally {
      handleCloseDialog();
    }
  }

  async function filterEmpresasByCategory(category) {
    // Verificar se a categoria foi encontrada e obter os ids das empresas associadas
    if (!category || empresas.length === 0) {
      return;
    }

    const categorias = category.categorias_id;
    const empresasValidasIds = [];

    for (const categoria of categorias) {
      const isValida = await verificaCategoria(categoria);
      const categoriaData = isValida.data

      if (categoriaData && categoriaData.status === 1 && categoriaData.empresa) {
        empresasValidasIds.push(categoriaData.empresa.id)
      }
    }

    const empresasUnicas = [...new Set(empresasValidasIds)];
    setEmpresasAssociadas(
        empresas.filter(empresa => empresasUnicas.includes(empresa.id))
    );
  }

  const onSubmit = async (data) => {
    if (data && (activeStep === 3 || (isEditAction && activeStep === 2))) {
      // remover variacoes que nao foram usadas

      exibirDialog('Salvando...');

      setIsLoading(true);

      for (const key in data.variacoes) {
        const variacao = data.variacoes[key];

        const shouldRemove = variacao.is_new === true && variacao.ativo === false;

        if (shouldRemove) {
          delete data.variacoes[key];
        }
      }

      if (!data.ca) {
        delete data.ca;
        delete data.validade_ca;
      } else if (data.validade_ca) {
        // force convert validade_ca to YYYY-MM-DD
        // solicitação do para remover hora da data e ignorar utc
        data.validade_ca = dayjs(data.validade_ca).format('YYYY-MM-DD');
      }

      let idGrupo = null;
      if (isEditAction) {
        idGrupo = data.id;
        delete data.id;

        // transform vida_util into array
        data.vida_util = [data.vida_util];
      } else {
        // formatação utilizada para POST (cadastro), onde tem manipulação de empresas
        // formata o id_empresas para se tornar uma array com os ids_empresas selecionados
        // so existe array id_empresas no cadastro
        for (const key in data.id_empresas) {
          if (!data.id_empresas[key]) {
            delete data.id_empresas[key];
          }
        }
        data.id_empresas = Object.keys(data.id_empresas).map(Number);
      }

      // Processar categorias com await
      const novasCategorias = [];
      for (const categoria of categorias) {
        if (categoria.nome === data.id_categorias) {
          for (const c of categoria.categorias_id) {
            const isValida = await verificaCategoria(c);
            const categoriaData = isValida.data;
            if (categoriaData && categoriaData.status === 1) {
              novasCategorias.push(categoriaData.id);
            }
          }
        }
      }
      data.id_categorias = novasCategorias;

      try {
        exibirDialog('Realizando operação. Aguarde...');

        if (data.productImage instanceof File) {
          // change name of variable productImage to img
          data.img = data.productImage;
        }
        delete data.productImage;

        // convert values to 1 or 0
        data.exibirRelatorio = data.exibirRelatorio ? 1 : 0;
        data.for_machine = data.for_machine ? 1 : 0;
        data.for_locker = data.for_locker ? 1 : 0;
        data.enviar_alerta = data.enviar_alerta ? 1 : 0;

        // convert data.variacoes in array
        const variacoes = [];
        for (const id_empresa in data.variacoes) {
          for (const key in data.variacoes[id_empresa]) {
            const variacao = data.variacoes[id_empresa][key];

            if (variacao.rotulo) {
              variacoes.push({
                rotulo: variacao.rotulo,
                id_variacao: variacao.id_variacao,
                id_empresa: variacao.id_empresa,
                ativo: !!variacao.ativo,
                cod_externo: variacao.cod_externo,
                is_dirty: variacao.is_dirty ?? false,
                is_new: variacao.is_new ?? true,
              });
            }
          }
        }

        if (data.id_unidadeMedida) {
          const unidadesValidas = {};
          for (const key in data.id_unidadeMedida) {
            if (data.id_unidadeMedida[key] !== undefined && data.id_unidadeMedida[key] !== null) {
              unidadesValidas[key] = data.id_unidadeMedida[key];
            }
          }
          data.id_unidadeMedida = unidadesValidas;
        }

        if (data.vida_util) {
          const vidaUtilValida = {};
          for (const key in data.vida_util) {
            if (data.vida_util[key] !== undefined && data.vida_util[key] !== null) {
              vidaUtilValida[key] = data.vida_util[key];
            }
          }
          data.vida_util = vidaUtilValida;
        }

        const formData = new FormData();

        variacoes.forEach((item, index) => {
          formData.append(`variacoes[${index}][nome]`, item.rotulo);
          formData.append(`variacoes[${index}][id_variacao]`, item.id_variacao);
          formData.append(`variacoes[${index}][id_empresa]`, item.id_empresa);
          formData.append(`variacoes[${index}][ativo]`, item.ativo);
          formData.append(`variacoes[${index}][cod_externo]`, item.cod_externo);
          if (isEditAction) {
            formData.append(`variacoes[${index}][is_new]`, !!item.is_new);
          }
        });
        delete data.variacoes;

        const gruposSelecionadosCriarPermissao = data.gruposSelecionadosCriarPermissao;
        if (gruposSelecionadosCriarPermissao && gruposSelecionadosCriarPermissao.length > 0) {
          gruposSelecionadosCriarPermissao.forEach((grupo) => {
            formData.append(`gruposSelecionadosCriarPermissao[${grupo.id}][id]`, grupo.id);
            formData.append(`gruposSelecionadosCriarPermissao[${grupo.id}][status]`, grupo.status ? 1 : 0);
          });
          delete data.gruposSelecionadosCriarPermissao;
          delete data.existsGheWithThisCategory;
        }

        for (const idEmpresa in data.id_unidadeMedida) {
          formData.append(`id_unidadeMedida[${idEmpresa}]`, data.id_unidadeMedida[idEmpresa]);
        }

        for (const idEmpresa in data.vida_util) {
          formData.append(`vida_util[${idEmpresa}]`, data.vida_util[idEmpresa]);
        }

        delete data.id_unidadeMedida;
        delete data.vida_util;

        // Append other data fields
        for (const key in data) {
          if (Array.isArray(data[key])) {
            data[key].forEach((item, index) => {
              formData.append(`${key}[${index}]`, item);
            });
          } else {
            formData.append(key, data[key]);
          }
        }

        if (isEditAction) {
          await atualizaProduto(idGrupo, formData);
          exibirAlerta('Edição de Produto', `${data.nome} editado com sucesso!`, 'success');
        } else {
          await cadastrarProduto(formData);
          exibirAlerta('Cadastro de Produto', `${data.nome} cadastrado com sucesso!`, 'success');
        }

        queryClient.invalidateQueries({ queryKey });
        setOpen(false);
      } catch (error) {
        console.log('onSubmit ~ error:', error);
        const action = isEditAction ? 'Edição' : 'Cadastro';
        const messageError = error.response?.data?.message ?? `Ocorreu um erro ao efetuar a ${action.toLowerCase()}.`;
        exibirAlerta(`${action} de Produto`, messageError, 'error');
      } finally {
        handleCloseDialog();
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
      exibirAlerta('Cadastro de Produto', 'Houve algum erro ao efetuar o cadastro.', 'error')
    }

    handleCloseDialog();
  };

  const validaCamposStepProductsInfo = async () => {
    const caPreenchido = Boolean(getValues('ca'));
    const validadeCa = getValues('validade_ca');
    const dataValidade = dayjs(validadeCa);

    if (caPreenchido && (!validadeCa || !dataValidade.isValid())) {
      setError('validade_ca', { type: 'required', message: 'A data de validade é obrigatória' });
      return false;
    } else if (caPreenchido && dataValidade.isValid() && dataValidade.isBefore(dayjs(), 'day')) {
      setError('validade_ca', { type: 'invalid', message: 'Equipamento com CA vencido' });
      return false;
    } else {
      clearErrors('validade_ca');
    }

    if (isEditAction) {
      const vidaUtil = getValues('vida_util');
      const unidadeMedida = getValues('id_unidadeMedida');

      if (!vidaUtil) {
        setError('vida_util', {type: 'required', message: 'A vida útil é obrigatória'});
        return false;
      } else if (!unidadeMedida) {
        setError('id_unidadeMedida', {type: 'required', message: 'A unidade de medida é obrigatória'});
      } else if (vidaUtil < 1) {
        setError('vida_util', { type: 'min', message: 'A vida útil deve ser maior que 0' });
        return false;
      }
    }

    const isValidNome = await trigger('nome');
    const isValidDescricao = await trigger('descricao');
    const isValidIndicacao = await trigger('indicacao');
    const isValidCategoria = await trigger('id_categorias');
    const isValidUnidade = await trigger('id_unidadeMedida');
    const isValidVidaUtil = await trigger('vida_util');

    const state =
      isValidNome &&
      isValidDescricao &&
      isValidIndicacao &&
      isValidCategoria &&
      isValidUnidade &&
      isValidVidaUtil;

    // etapa apenas para cadastro
    if (!isEditAction && state) {
      const nomeCategoria = getValues('id_categorias');
      let idCategorias = [];

      categorias.forEach(categoria => {
        if (categoria.nome === nomeCategoria) {
          idCategorias = categoria.categorias_id;
        }
      });

      const res = await listaGruposByCategoria(idCategorias);
      const grupos = res.data.grupos;
      setGHEs(grupos);

      if (grupos.length > 0) {
        // Foram encontrados GHE's relacionados a essa categoria
        grupos.forEach((grupo) => {
          setValue(`gruposSelecionadosCriarPermissao[${grupo.id}][id]`, grupo.id);
          setValue(`gruposSelecionadosCriarPermissao[${grupo.id}][status]`, false);
        })

        setValue('existsGheWithThisCategory', true);
      }
    }

    return state;
  };

  const validaCamposStepProductVariations = async () => {
    const variacoes = getValues('variacoes');
    let variacaoAtiva = false;
    let erroCodigo = false;

    // Normaliza o código: trim + uppercase. Retorna '' para vazio.
    const normalizaCod = (v) => String(v ?? '').trim().toUpperCase();

    // Considera "N/A" (em qualquer capitalização) como não-duplicável.
    const isIgnoravel = (cod) => cod === '' || cod === 'N/A';

    // 1) Limpa erros e detecta duplicatas DENTRO do formulário, por empresa.
    const ocorrencias = {};

    for (const id_empresa in variacoes) {
      for (const chave in variacoes[id_empresa]) {
        clearErrors(`variacoes.${id_empresa}.${chave}.cod_externo`);

        const variation = variacoes[id_empresa][chave];
        if (!variation.ativo) continue;

        variacaoAtiva = true;

        const cod = normalizaCod(variation.cod_externo);

        // código vazio numa variação ativa continua sendo erro
        if (cod === '') {
          erroCodigo = true;
          setError(`variacoes.${id_empresa}.${chave}.cod_externo`, {
            type: 'required',
            message: 'É preciso informar o código',
          });
          continue;
        }

        // N/A não entra na checagem de duplicidade
        if (isIgnoravel(cod)) continue;

        if (!ocorrencias[id_empresa]) ocorrencias[id_empresa] = {};
        if (!ocorrencias[id_empresa][cod]) ocorrencias[id_empresa][cod] = [];
        ocorrencias[id_empresa][cod].push(chave);
      }
    }

    // Marca erro em toda variação que compartilha código com outra na mesma empresa.
    for (const id_empresa in ocorrencias) {
      for (const cod in ocorrencias[id_empresa]) {
        const chaves = ocorrencias[id_empresa][cod];
        if (chaves.length > 1) {
          erroCodigo = true;
          chaves.forEach((chave) => {
            setError(`variacoes.${id_empresa}.${chave}.cod_externo`, {
              type: 'unique',
              message: 'O código deve ser único dentro da empresa.',
            });
          });
        }
      }
    }

    // Se já há duplicata local, nem vai ao backend.
    if (erroCodigo) {
      exibirAlerta(
          'Preenchimento do formulário',
          'Há códigos duplicados entre as variações. Cada código deve ser único dentro da empresa.',
          'error'
      );
      return false;
    }

    // 2) Checagem de unicidade no backend (só para os que mudaram).
    for (const id_empresa in variacoes) {
      for (const chave in variacoes[id_empresa]) {
        const variation = variacoes[id_empresa][chave];
        if (!variation.ativo) continue;

        const codigoAtual = normalizaCod(variation.cod_externo);
        const codigoOriginal = normalizaCod(variation.cod_externo_og);

        if (isIgnoravel(codigoAtual)) continue;
        if (!(variation.is_dirty && codigoAtual !== codigoOriginal)) continue;

        try {
          const filter = { cod_externo: codigoAtual, id_empresas: [id_empresa] };
          const res = await listaProdutos(filter);
          const produto = res.data.data;

          if (produto.length > 0) {
            erroCodigo = true;
            setError(`variacoes.${id_empresa}.${chave}.cod_externo`, {
              type: 'unique',
              message: 'Código já cadastrado.',
            });
          }
        } catch (error) {
          console.error(error);
        }
      }
    }

    if (!variacaoAtiva && selectedProduct?.status) {
      exibirAlerta(
          'Preenchimento do formulário',
          'É necessário marcar ao menos um tamanho/variação e informar seus respectivos códigos.',
          'error'
      );
      return false;
    }

    if (selectedProduct?.status) return variacaoAtiva && !erroCodigo;
    return !erroCodigo;
  };

  const validaStepCompaniesSelection = () => {
    const empresasSelecionadas = Object.entries(getValues('id_empresas')).filter(([key, value]) => value);

    if (empresasSelecionadas.length === 0) {
      exibirAlerta(
          'Preenchimento do formulário',
          'É necessário selecionar ao menos uma empresa.',
          'error'
      );
      return false;
    }

    let temErro = false;

    for (const [key] of empresasSelecionadas) {
      const vidaUtil = getValues(`vida_util.${key}`);
      const unidadeMedida = getValues(`id_unidadeMedida.${key}`);
      const permissao = getValues(`isCheckedPermission.${key}`);

      let erroAtual = false;
      let mensagemErro = '';

      // valida vida útil
      if (!vidaUtil) {
        setError(`vida_util.${key}`, {
          type: 'required',
          message: 'A vida útil é obrigatória',
        });
        erroAtual = true;
        temErro = true;
        mensagemErro = 'É necessário preencher a vida útil.';
      } else if (vidaUtil < 1) {
        setError(`vida_util.${key}`, {
          type: 'min',
          message: 'A vida útil deve ser maior que 0',
        });
        erroAtual = true;
        temErro = true;
        mensagemErro = 'A vida útil deve ser maior que 0.';
      }

      // valida unidade de medida
      if (!unidadeMedida) {
        setError(`id_unidadeMedida.${key}`, {
          type: 'required',
          message: 'A unidade de medida é obrigatória',
        });
        erroAtual = true;
        temErro = true;
        mensagemErro = mensagemErro
            ? `${mensagemErro} E também preencher a unidade de medida.`
            : 'É necessário preencher a unidade de medida.';
      }

      // valida permissão
      if (!permissao) {
        setError(`isCheckedPermission.${key}`, {
          type: 'required',
          message: 'Você deve confirmar a designação automática antes de continuar.',
        });
        erroAtual = true;
        temErro = true;
        mensagemErro = mensagemErro
            ? `${mensagemErro} Além disso, confirme a designação automática.`
            : 'Você deve confirmar a designação automática antes de continuar.';
      }

      if (erroAtual) {
        exibirAlerta('Preenchimento do formulário', mensagemErro, 'error');
        return false;
      }
    }

    return !temErro;
  };

  const validateStepFunctions = isEditAction ? [
    validaCamposStepProductsInfo,
    validaCamposStepProductVariations,
    () => true
  ] : [
    validaCamposStepProductsInfo,
    validaStepCompaniesSelection,
    validaCamposStepProductVariations,
    () => true
  ];

  const handleNext = async (event) => {
    event.preventDefault();
    try {
      exibirDialog('Validando campos. Aguarde...');
      const isStepValid = await validateStepFunctions[activeStep]();
      if (isStepValid) {
        setActiveStep(activeStep + 1);
      }
    } catch (error) {
      exibirAlerta('Ops', 'Houve um erro ao validar os campos.', 'error');
    } finally {
      handleCloseDialog();
    }
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const handleStep = (pos) => {
    if (pos < activeStep) {
      setActiveStep(pos);
    }
  }

  const steps = isEditAction
    ? ['Dados do Produto', 'Tamanhos/Variações', 'Cadastro de Produtos']
    : ['Dados do Produto', 'Empresas Relacionadas', 'Tamanhos/Variações', 'Cadastro de Produtos'];

  useEffect(() => {
    if (open) {
      const defaultValues = {
        id: selectedProduct?.id || '',
        nome: selectedProduct?.nome || '',
        descricao: selectedProduct?.descricao || '',
        ca: selectedProduct?.ca || '',
        validade_ca: selectedProduct && selectedProduct.validadeCa && dayjs.utc(convertToIsoDate(selectedProduct.validadeCa)),
        indicacao: selectedProduct?.indicacao || '',
        id_categorias: selectedProduct?.categoria?.nome || '',
        id_unidadeMedida: selectedProduct?.unidadeMedida?.id || '',
        // id_estoque: selectedProduct?.estoque?.id || '',
        variacoes: selectedProduct?.variacoes || [],
        precaucao: selectedProduct?.precaucao || '',
        observacao: selectedProduct?.observacao || '',
        productImage: selectedProduct?.img || '',
        exibirRelatorio: selectedProduct ? selectedProduct.exibirRelatorio : true,
        enviar_alerta: selectedProduct ? selectedProduct.enviarAlerta : true,
        for_machine: selectedProduct ? selectedProduct.forMachine : false,
        for_locker: selectedProduct ? selectedProduct.forLocker : false,
      };

      if (isEditAction) {
        // o campo vida_util é tratado separadamente na edição
        defaultValues.vida_util = selectedProduct?.vida_util || '';

        // como na edição, não acessa o step de empresas relacionadas, 
        // utiliza-se essa prop no StepProductConfirm para exibição da empresa relacionada ao Produto
        if (selectedProduct?.idEmpresa) {
          defaultValues.id_empresa = selectedProduct.idEmpresa;
          defaultValues.id_empresas = []
          defaultValues.id_empresas[selectedProduct.idEmpresa] = selectedProduct.idEmpresa;
        }

        if (selectedProduct?.filhos) {
          defaultValues.variacoes[selectedProduct.idEmpresa] = [];

          selectedProduct.filhos.forEach(filho => {
            defaultValues.variacoes[selectedProduct.idEmpresa][filho.variacao.id] = {
              rotulo: filho.variacao.tipo,
              id_variacao: filho.variacao.id,
              id_empresa: filho.idEmpresa,
              cod_externo: filho.cod_externo,
              cod_externo_og: filho.cod_externo,
              ativo: !!filho.status,
              is_dirty: false,
              is_new: false,
            };
          });
        }

        const selectedCategory = categorias.find(categoria => categoria.categorias_id[0] === selectedProduct.categoria.id);
        setSelectedCategory(selectedCategory);
        setEmpresasAssociadas(empresas.filter(empresa => selectedProduct.idEmpresa === empresa.id));
      }

      reset(defaultValues);

      loadInitialData(defaultValues.id_empresa);
    } else {
      reset();
      setExibiAlertaAutoPreenchimento(false);
      setActiveStep(0);
      setSelectedCategory(null);
      setEmpresasAssociadas([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reset, selectedProduct]);

  useEffect(() => {
    filterEmpresasByCategory(selectedCategory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  return (
    <Dialog open={open} fullWidth maxWidth={'lg'}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>Cadastrar novo Produto</DialogTitle>
        <DialogContentText sx={{ m: 2 }}>
          Preencha corretamente os dados abaixo para cadastrar um novo Produto.
        </DialogContentText>
        <Stepper activeStep={activeStep} sx={{ px: 3 }} orientation={isMobile ? 'vertical' : 'horizontal'}>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepButton color="inherit" onClick={() => handleStep(index)}>
                <StepLabel>{label}</StepLabel>
              </StepButton>
            </Step>
          ))}
        </Stepper>
        <DialogContent sx={{ minHeight: '65vh' }}>
          <Box sx={{ p: 2, backgroundColor: '#fafafa' }} >
            <FormControl variant="standard" fullWidth >
              {activeStep === 0 &&
                <StepProductsInfo
                  control={control}
                  errors={errors}
                  setError={setError}
                  register={register}
                  setValue={setValue}
                  getValues={getValues}
                  watch={watch}
                  trigger={trigger}
                  unidadesDeMedida={unidadesDeMedida}
                  categorias={categorias}
                  exibiAlertaAutoPreenchimento={exibiAlertaAutoPreenchimento}
                  setExibiAlertaAutoPreenchimento={setExibiAlertaAutoPreenchimento}
                  clearErrors={clearErrors}
                  selectedProduct={selectedProduct}
                  setSelectedCategory={setSelectedCategory}
                  isEditAction={isEditAction}
                  actionsAllowed={allowInteraction}
                  empresasAssociadas={empresasAssociadas}
                />
              }
              {(!isEditAction && activeStep === 1) &&
                <StepCompaniesSelection
                  empresasAssociadas={empresasAssociadas}
                  GHEs={GHEs}
                  getValues={getValues}
                  setValue={setValue}
                  register={register}
                  setError={setError}
                  errors={errors}
                  clearErrors={clearErrors}
                  unidadesDeMedida={unidadesDeMedida}
                  control={control}
                />
              }
              {((isEditAction && activeStep === 1) || (!isEditAction && activeStep === 2)) &&
                <StepProductVariations
                  empresasAssociadas={empresasAssociadas}
                  GHEs={GHEs}
                  getValues={getValues}
                  setValue={setValue}
                  register={register}
                  setError={setError}
                  errors={errors}
                  clearErrors={clearErrors}
                  handleExibirAlerta={exibirAlerta}
                  selectedProduct={selectedProduct}
                />
              }
              {((isEditAction && activeStep === 2) || (!isEditAction && activeStep === 3)) &&
                <StepProductConfirm
                  empresas={empresas}
                  empresasAssociadas={empresasAssociadas}
                  GHEs={GHEs}
                  setValue={setValue}
                  getValues={getValues}
                  isEditAction={isEditAction}
                  unidadesDeMedida={unidadesDeMedida}
                />
              }
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} variant="contained">
            Cancelar
          </Button>
          <Divider orientation="vertical" flexItem sx={{ ml: 1 }} />
          <Button onClick={handleBack} variant="outlined" type="button" disabled={activeStep === 0}>
            Voltar
          </Button>
          {activeStep !== steps.length - 1 ? (
            <Button onClick={handleNext} variant="contained" type="button" disabled={unidadesDeMedida.length === 0 || categorias.length === 0}>
              Próximo
            </Button>
          ) :
            actionsAllowed && (
              <Button type="submit" variant="contained" disabled={isLoading}>
                Salvar
              </Button>
            )}
        </DialogActions>
      </form>
    </Dialog>
  );
}